import React, { useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import moment from 'moment';
import { Formik, FormikActions } from 'formik';
import {
  Input,
  Toast,
  Button,
  Radio,
  Checkbox,
  FormMessage,
  theme,
  FileUpload,
  FilePreview,
  Text,
  Flex,
  Label,
} from '@xometry/xometry_loft';
import { kind } from '@xometry/xometry_loft/dist/theme';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import MomentLocaleUtils from 'react-day-picker/moment';
import { css } from '@emotion/react';
import dayPickerStyles from './dayPickerStyles';
import { states, exemptions } from './helpers';
import { AddTaxExemptionUrlQuery } from '../../schema/generated/schema';

import taxExemptionApi from '../../services/taxexemption';
import { initialValues, schema, TaxExemptionFormValues } from './formikSetup';
import { GET_ORIGIN } from '../../config';

const addingsettings_page = css`
  margin: 80px 40px;
  padding: 16px;
  background-color: white;
  border: solid 1px #d8dee1;
  color: rgb(60, 60, 60);
  @media (max-width: 640px) {
    margin: 80px 16px;
  }
`;

const formSection = css`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${theme.space[1]};
`;

const radioLabel = css`
  font-size: 16px;
  margin-left: 8px;
`;

const textInput = css`
  width: 240px;
  height: 32px;
  border-radius: 5px;
  border-style: solid;
  border-width: 1px;
  padding-left: 8px;
  font-size: 16px;
`;

const selectionSection = css`
  max-height: 350px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  margin-left: 12px;
  padding-top: ${theme.space[1]};

  @media (max-width: 1024px) {
    max-height: 600px;
  }
  @media (max-width: 576px) {
    max-height: unset;
  }
`;

const dayPickerInput = css`
  input[datainvalid='true'] {
    border-color: #9e3c3c !important;
    box-shadow: 0 0 4px 0 #9e3c3c;
  }
  .DayPickerInput-OverlayWrapper {
    position: relative;
  }
  .DayPicker-Day--today {
    color: ${theme.colors.blues[100]};
    font-weight: bold;
  }
  .DayPicker-Day {
    line-height: 17px;
  }
`;

const ErrorNotification = ({ errors, touched, fieldName }) => {
  let errorMssg = errors[fieldName];
  if (typeof errors[fieldName] === 'object') {
    const errorArray = [];
    Object.keys(errors[fieldName]).forEach((k) => {
      errorArray.push(errors[fieldName][k]);
    });
    errorMssg = errorArray.join(', ');
  }

  return errorMssg && touched[fieldName] ? (
    <FormMessage
      css={css`
        margin-top: 4px;
      `}
      invalid
    >
      {errorMssg}
    </FormMessage>
  ) : null;
};

const formatDayChange = (selectedDay, disabled) => {
  if (disabled || !selectedDay) {
    // If Day is disabled or empty, return empty string
    return '';
  }
  return MomentLocaleUtils.formatDate(selectedDay.toISOString(), 'MM-DD-YYYY');
};

// Tax Exemption Function Component //
const TaxExemption = () => {
  const params = useParams();
  const location = useLocation();

  const [formAlertFlag, setFormAlertFlag] = useState<boolean>(false);
  const [formAlert, setFormAlert] = useState({
    type: theme.kind.danger,
    messages: new Array<string>(),
  });
  const [saving, setSaving] = useState<boolean>(false);
  const [selectAllStates, setSelectAllStates] = useState<boolean>(false);
  const [uploadedTaxDocument, setUploadedTaxDocument] = useState<File[]>([]);

  const stateIds = states.map((state) => state.id);
  const userSettingsUrl = `/user-settings${
    params.userid ? `/${params.userid}` : ''
  }`;

  const [addTaxExemptionUrl] = useMutation(AddTaxExemptionUrlQuery);

  // Redirect if billing account data wasn't passed in
  if (!location.state || !location.state.billingAccount) {
    window.location.assign(`${GET_ORIGIN}/404`);
    return null;
  }

  // Get billing account passed in from link component
  const linkedBillingAccountId = location.state.billingAccount.id;
  const billingAccountName = location.state.billingAccount.name;

  const setAlert = (type: kind, messages: string[]) => {
    const formAlert = { type, messages };
    setFormAlertFlag(true);
    setFormAlert(formAlert);

    if (type === 'danger') {
      window.scrollTo(0, 0);
    }
  };

  const closeAlert = () => {
    setFormAlertFlag(false);
  };

  const getExemptionCheckboxes = (
    values: TaxExemptionFormValues,
    setFieldValue: FormikActions<TaxExemptionFormValues>['setFieldValue']
  ) =>
    exemptions.map((exempt) => (
      <div
        css={css`
          margin-bottom: 8px;
        `}
        key={`exempt-${exempt.id}-${exempt.name}`}
      >
        <Radio
          name="exemptionType"
          id={`exempt-${exempt.id}`}
          value={exempt.name}
          checked={values.exemption === exempt.id}
          onChange={() => {
            setFieldValue('exemption', exempt.id);
          }}
        />
        <label htmlFor={`exempt-${exempt.id}`} css={radioLabel}>
          {exempt.name}
        </label>
      </div>
    ));

  const getStateCheckboxes = (
    values: TaxExemptionFormValues,
    setFieldValue: FormikActions<TaxExemptionFormValues>['setFieldValue']
  ) => {
    const tempSelectedStates = [...values.selectedStates];

    return states.map((state) => (
      <div key={`state-${state.id}`}>
        <Checkbox
          id={`state-${state.id}`}
          value={state.id}
          checked={tempSelectedStates.includes(state.id)}
          onChange={() => toggleCheckbox(values, setFieldValue, state.id)}
        />
        <label
          htmlFor={`state-${state.id}`}
          css={css`
            font-size: 16px;
            padding-left: ${theme.space[1]};
          `}
        >
          {state.name}
        </label>
      </div>
    ));
  };

  // Converting the PDF to base64 is an asynchronous task
  const handleFileBase64Encoding = (taxExemptionFile: File) => {
    return new Promise((resolve) => {
      // FileReader function for read the file.
      const fileReader = new FileReader();
      // Convert data to base64
      fileReader.readAsDataURL(taxExemptionFile);
      // When data is converted onload is triggered. Use response to resolve the promise.
      fileReader.onload = (fileLoadedEvent) => {
        if (!fileLoadedEvent?.target?.result) return;
        const dataUrl = fileLoadedEvent.target.result as string;
        // Remove the Data-URL declaration preceding the Base64-encoded data via split
        resolve(dataUrl.split(',')[1]);
      };
    });
  };

  const toggleCheckbox = (
    values: TaxExemptionFormValues,
    setFieldValue: FormikActions<TaxExemptionFormValues>['setFieldValue'],
    stateId: number
  ) => {
    const tempSelectedStates = [...values.selectedStates];
    tempSelectedStates.includes(stateId)
      ? tempSelectedStates.splice(tempSelectedStates.indexOf(stateId), 1)
      : tempSelectedStates.push(stateId);
    setFieldValue('selectedStates', tempSelectedStates);
  };

  // Toggle selection of all states
  const toggleSelectAllStates = (
    setFieldValue: FormikActions<TaxExemptionFormValues>['setFieldValue']
  ) => {
    const selectedValue = !selectAllStates ? stateIds : [];
    setFieldValue('selectedStates', selectedValue);
    setSelectAllStates(!selectAllStates);
  };

  const submitForm = (values: TaxExemptionFormValues) => {
    // Use passed in billing account if it was provided from link component
    const billingAccountId = linkedBillingAccountId || values.billingAccountId;

    const {
      selectedStates,
      base64EncodedTaxPDF,
      taxNumber,
      exemption,
      expirationDate,
      signedDate,
      signerName,
      signerTitle,
    } = values;
    closeAlert();

    // Convert date fields to the expected format for CertCapture API
    const expiration_date = moment(expirationDate, 'MM-DD-YYYY').format(
      'YYYY-MM-DD'
    );
    const signed_date = moment(signedDate, 'MM-DD-YYYY').format('YYYY-MM-DD');

    const formattedStates = selectedStates.map((selectedState: number) => {
      let formattedState;
      states.some((state) => {
        if (state.id === selectedState) {
          formattedState = state;
          return true;
        }
        return false;
      });
      return formattedState;
    });

    // Build payload to be sent to the Cert Creation API
    const payload = {
      expiration_date,
      signed_date,
      signer_title: signerTitle,
      signer_name: signerName,
      tax_number: taxNumber,
      pdf: base64EncodedTaxPDF,
      exposureZone: formattedStates,
      expected_tax_code: { id: exemption },
    };

    setSaving(true);
    addTaxExemptionUrl({
      variables: {
        input: {
          billingAccountId,
        },
      },
    })
      .then((response) => {
        if (response.data.addTaxExemptionUrl) {
          taxExemptionApi
            .addTaxExemption(response.data.addTaxExemptionUrl, payload)
            .then(() => {
              setAlert(theme.kind.success, [
                'Successfully created Certification!',
              ]);
              if (window.analytics) {
                window.analytics.track('Tax Exemption Added', {
                  staffCreated: params.userid ? 'Yes' : 'No',
                });
              }
              window.location.href = userSettingsUrl;
              setSaving(false);
            })
            .catch((e) => {
              console.error(e);
              setAlert(theme.kind.danger, [e.message]);
              setSaving(false);
            });
        } else {
          setAlert(theme.kind.danger, [
            'There was an error setting up your tax exemption, please wait and try again. If this problem persists, please contact us at support@xometry.com',
          ]);
          setSaving(false);
        }
      })
      .catch((e) => {
        const errorMssgs = e.graphQLErrors.map((errorObj) => errorObj.message);
        setAlert(theme.kind.danger, errorMssgs);
        setSaving(false);
      });
  };

  return (
    <div css={addingsettings_page}>
      <Toast isOpen={formAlertFlag} kind={formAlert.type} onClose={closeAlert}>
        <span
          css={css`
            font-weight: bold;
            margin-right: 8px;
          `}
        >
          {formAlert.type !== 'success' ? 'Error:' : ''}
        </span>
        {formAlert.messages.join(', ')}
      </Toast>
      <Formik
        initialValues={initialValues}
        onSubmit={submitForm}
        validationSchema={schema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          handleSubmit,
          isValid,
        }) => (
          <form
            onSubmit={(e) => {
              if (!isValid) {
                setAlert(theme.kind.danger, [
                  'Please fix the below fields before submitting again.',
                ]);
              }

              handleSubmit(e);
            }}
          >
            <Flex gap={theme.space[5]} flexDirection="column">
              <Text kind="body-title">
                Add Tax Exemption Certification to{' '}
                <span className="pii-encrypt">{billingAccountName}</span>
              </Text>
              {/* Tax File Upload Section */}
              <div>
                <Text kind="label" display="inline">
                  Upload Exemption Form{' '}
                </Text>
                <Text kind="body-sm">(PDF Only)</Text>
                {uploadedTaxDocument.length > 0 ? (
                  <FilePreview
                    detailView
                    clientFile={uploadedTaxDocument[0]}
                    onFileRemove={() => {
                      setFieldValue('base64EncodedTaxPDF', '');
                      setUploadedTaxDocument([]);
                    }}
                  />
                ) : (
                  <FileUpload
                    fileList={uploadedTaxDocument}
                    accept={['.pdf']}
                    // 7MB nginx upload limit
                    // https://github.com/xometry/billing-account-service/blob/main/charts/billing-account-service/values.yaml#L85
                    sizeLimit={7340032}
                    fileLimit={1}
                    onFileSelected={async (selectedFile) => {
                      const fileToUpload = selectedFile[0].clientFile;
                      const pdf = await handleFileBase64Encoding(fileToUpload);
                      setFieldValue('base64EncodedTaxPDF', pdf);
                      setUploadedTaxDocument([fileToUpload]);
                    }}
                  />
                )}
                <ErrorNotification
                  errors={errors}
                  touched={touched}
                  fieldName="base64EncodedTaxPDF"
                />
              </div>

              <div css={formSection}>
                <Label htmlFor="expirationDate">
                  Exemption Expiration Date
                </Label>
                <div css={[dayPickerStyles.calendar, dayPickerInput]}>
                  <DayPickerInput
                    formatDate={MomentLocaleUtils.formatDate}
                    parseDate={MomentLocaleUtils.parseDate}
                    format="MM-DD-YYYY"
                    placeholder={`01-15-${new Date().getFullYear() + 1}`}
                    dayPickerProps={{
                      disabledDays: { before: new Date() },
                    }}
                    value={values.expirationDate || ''}
                    onDayChange={(day, { disabled }) =>
                      setFieldValue(
                        'expirationDate',
                        formatDayChange(day, disabled)
                      )
                    }
                    inputProps={{
                      name: 'expirationDate',
                      id: 'expirationDate',
                      autoComplete: 'off',
                      style: {
                        width: '240px',
                        height: '32px',
                        border: `1px solid ${theme.colors.darkBlues[50]}`,
                        borderRadius: '5px',
                        fontSize: '16px',
                        padding: '6px',
                        paddingLeft: '8px',
                        color: 'black',
                        backgroundColor: 'white',
                        outline: 0,
                        display: 'block',
                      },
                    }}
                  />
                </div>
                <ErrorNotification
                  errors={errors}
                  touched={touched}
                  fieldName="expirationDate"
                />
              </div>
              <div css={formSection}>
                <Label htmlFor="signedDate">Exemption Effective Date</Label>
                <div css={[dayPickerStyles.calendar, dayPickerInput]}>
                  <DayPickerInput
                    formatDate={MomentLocaleUtils.formatDate}
                    parseDate={MomentLocaleUtils.parseDate}
                    format="MM-DD-YYYY"
                    placeholder={`01-15-${new Date().getFullYear() - 1}`}
                    value={values.signedDate || ''}
                    onDayChange={(day, { disabled }) =>
                      setFieldValue(
                        'signedDate',
                        formatDayChange(day, disabled)
                      )
                    }
                    inputProps={{
                      name: 'signedDate',
                      id: 'signedDate',
                      autoComplete: 'off',
                      style: {
                        width: '240px',
                        height: '32px',
                        border: `1px solid ${theme.colors.darkBlues[50]}`,
                        borderRadius: '5px',
                        fontSize: '16px',
                        padding: '6px',
                        paddingLeft: '8px',
                        color: 'black',
                        backgroundColor: 'white',
                        outline: 0,
                        display: 'block',
                      },
                    }}
                  />
                </div>
                <ErrorNotification
                  errors={errors}
                  touched={touched}
                  fieldName="signedDate"
                />
              </div>
              <div css={formSection}>
                <Label htmlFor="taxNumber">Tax Number</Label>
                <Input
                  className="pii-block"
                  invalid={errors.taxNumber && touched.taxNumber}
                  id="taxNumber"
                  name="taxNumber"
                  css={textInput}
                  type="text"
                  onChange={handleChange}
                />
                <ErrorNotification
                  errors={errors}
                  touched={touched}
                  fieldName="taxNumber"
                />
              </div>
              <div css={formSection}>
                <Label htmlFor="selectedStates">
                  Select the state(s)/territory in which you hold or want to
                  update your tax exemption.
                </Label>
                <ErrorNotification
                  errors={errors}
                  touched={touched}
                  fieldName="selectedStates"
                />
                <div
                  css={css`
                    margin-left: 12px;
                    margin-bottom: 12px;
                  `}
                >
                  <Checkbox
                    id="state-select-all"
                    value={selectAllStates}
                    onClick={() => toggleSelectAllStates(setFieldValue)}
                  />
                  <label
                    htmlFor="state-select-all"
                    css={css`
                      font-size: 16px;
                      padding-left: ${theme.space[1]};
                    `}
                  >
                    {selectAllStates ? 'Unselect All' : 'Select All'}
                  </label>
                </div>
                <div css={selectionSection}>
                  {getStateCheckboxes(values, setFieldValue)}
                </div>
              </div>
              <div css={formSection}>
                <Label htmlFor="exemption">
                  What type of exemption is your buisness/organization qualified
                  or registered for?
                </Label>
                <ErrorNotification
                  errors={errors}
                  touched={touched}
                  fieldName="exemption"
                />
                <div css={selectionSection}>
                  {getExemptionCheckboxes(values, setFieldValue)}
                </div>
              </div>

              <div css={formSection}>
                <Text kind="label" paddingBottom={theme.space[1]}>
                  Authorization:
                </Text>
                <Label htmlFor="signerName">Authorized Signer Name</Label>
                <Input
                  className="pii-encrypt"
                  invalid={errors.signerName && touched.signerName}
                  id="signerName"
                  name="signerName"
                  css={textInput}
                  type="text"
                  onChange={handleChange}
                />
                <ErrorNotification
                  errors={errors}
                  touched={touched}
                  fieldName="signerName"
                />
              </div>
              <div css={formSection}>
                <Label htmlFor="signerTitle">Authorized Signer Title</Label>
                <Input
                  className="pii-encrypt"
                  invalid={errors.signerTitle && touched.signerTitle}
                  id="signerTitle"
                  name="signerTitle"
                  css={textInput}
                  type="text"
                  onChange={handleChange}
                />
                <ErrorNotification
                  errors={errors}
                  touched={touched}
                  fieldName="signerTitle"
                />
              </div>
              <Text kind="label" maxWidth="560px">
                By submitting this form to Xometry you are certifying its
                accuracy and are aware that Xometry will use this information
                when filing sales tax returns.
              </Text>
              <Flex justifyContent={'flex-end'} gap={theme.space[3]}>
                <Link to="/user-settings">
                  <Button scale="medium" kind={theme.kind.primaryOutline}>
                    Cancel
                  </Button>
                </Link>
                <Button
                  data-testid="create-cert-button"
                  scale="medium"
                  type="submit"
                  disabled={
                    saving ||
                    Boolean(
                      Object.keys(errors).length && Object.keys(touched).length
                    )
                  }
                  kind={theme.kind.primary}
                >
                  Submit
                </Button>
              </Flex>
            </Flex>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default TaxExemption;
